import { useDispatch, useSelector } from 'react-redux';
import './journal.scss';
import { useEffect, useState, useMemo } from 'react';
import { fetchBillets, filterBillets } from '../../data/slices/billetsSlice';
import UpdatedTable from '../../components/updated-table/StyledTable';
import { 
    Search as SearchIcon, 
    FileDownloadOutlined, 
    Refresh,
    AccountBalanceWallet,
    FilePresent,
    Assessment
} from "@mui/icons-material";
import { COLORS, jsonToXlsx, priceFormatterV3 } from '../../settings/themeSettings';
import { 
    Typography, 
    Box, 
    Button, 
    TextField, 
    Paper, 
    Stack, 
    InputAdornment,
    CircularProgress,
    Grid
} from '@mui/material';
import { BlobProvider } from '@react-pdf/renderer';
import _ from "lodash";
import JournalVentePDF from '../../components/JournalPDF/JournalVentePDF';
import { fetchPaiments } from '../../data/slices/paiementsSlice';
import DashboardTableIII from '../../components/DashboardTable/ReversedTable';
import PageHeader from '../../components/layout/PageHeader';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';

const Caisse = (props) => {
    const { billets, fetchBilletsStatus } = useSelector((state) => state.billets);
    const { paiements } = useSelector((state) => state.paiements);

    const dispatch = useDispatch();
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [billetsWithPayments, setBilletsWithPayments] = useState([]);
    const [paymentGroup, setPaymentGroup] = useState({});

    // Enhanced billets with payment method information
    const enhanceBilletsWithPayments = useMemo(() => {
        if (!billets || billets.length === 0) return [];
        if (!paiements || paiements.length === 0) {
            return billets.map(billet => ({ ...billet, methodePaiement: 'Chargement...' }));
        }

        return billets.map((billet) => {
            let paymentMethods = 'N/A';
            if (billet.numeroFacture) {
                const billetFactureNumber = String(billet.numeroFacture).trim();
                const matchingPayments = paiements.filter(p => String(p.facture).trim() === billetFactureNumber);
                if (matchingPayments.length > 0) {
                    paymentMethods = matchingPayments
                        .map(p => p.methode)
                        .filter(m => m && m.trim() !== '')
                        .filter((m, i, a) => a.indexOf(m) === i)
                        .join(' - ');
                }
            }
            
            if (paymentMethods === 'N/A' && billet.numerosFactures && billet.numerosFactures.length > 1) {
                const allMatchingPayments = [];
                billet.numerosFactures.forEach(fn => {
                    const bfNumber = String(fn).trim();
                    const matches = paiements.filter(p => String(p.facture).trim() === bfNumber);
                    allMatchingPayments.push(...matches);
                });
                
                if (allMatchingPayments.length > 0) {
                    paymentMethods = allMatchingPayments
                        .map(p => p.methode)
                        .filter(m => m && m.trim() !== '')
                        .filter((m, i, a) => a.indexOf(m) === i)
                        .join(' - ');
                }
            }
            
            return { ...billet, methodePaiement: paymentMethods || 'N/A' };
        });
    }, [billets, paiements]);

    useEffect(() => {
        dispatch(fetchBillets());
        dispatch(fetchPaiments());
    }, [dispatch]);

    useEffect(() => {
        if (!!startDate && !!endDate) {
            dispatch(fetchBillets({ startDate, endDate }));
            dispatch(fetchPaiments({ startDate, endDate }));
        }
    }, [startDate, endDate, dispatch]);

    useEffect(() => {
        setPaymentGroup(_.groupBy(paiements, "methode"));
    }, [paiements]);

    useEffect(() => {
        setBilletsWithPayments(enhanceBilletsWithPayments);
    }, [enhanceBilletsWithPayments]);

    const stats = useMemo(() => {
        const totalFacture = enhanceBilletsWithPayments.reduce((sum, b) => sum + (Number(b.tarif) || 0), 0);
        const totalEncaisse = enhanceBilletsWithPayments.reduce((sum, b) => sum + (Number(b.montantEncaisse) || 0), 0);
        const solde = totalFacture - totalEncaisse;
        return { totalFacture, totalEncaisse, solde };
    }, [enhanceBilletsWithPayments]);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <PageHeader 
                title="État de Caisse"
                subtitle="Suivi des encaissements et rapprochements bancaires"
                icon={AccountBalanceWallet}
                breadcrumbs={[
                    { label: 'Journaux', path: '/journal' },
                    { label: 'Caisse', path: '/journal/caisse' }
                ]}
                actions={[
                    { 
                        label: 'Exporter Excel', 
                        icon: <FileDownloadOutlined />, 
                        onClick: () => jsonToXlsx(billetsWithPayments, "Etat_Caisse"),
                        variant: 'outlined'
                    }
                ]}
            />

            {/* KPI Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: COLORS.gray[200], bgcolor: 'white' }}>
                        <Typography variant="subtitle2" sx={{ color: COLORS.text.secondary, fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>Total Facturé</Typography>
                        <Typography variant="h4" sx={{ color: COLORS.secondary, fontWeight: 800 }}>{stats.totalFacture.toLocaleString()} CFA</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: COLORS.success + '30', bgcolor: COLORS.success + '05' }}>
                        <Typography variant="subtitle2" sx={{ color: COLORS.success, fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>Total Encaissé</Typography>
                        <Typography variant="h4" sx={{ color: COLORS.success, fontWeight: 800 }}>{stats.totalEncaisse.toLocaleString()} CFA</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: stats.solde > 0 ? COLORS.error + '30' : COLORS.success + '30', bgcolor: stats.solde > 0 ? COLORS.error + '05' : COLORS.success + '05' }}>
                        <Typography variant="subtitle2" sx={{ color: stats.solde > 0 ? COLORS.error : COLORS.success, fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>Reste à Recouvrer</Typography>
                        <Typography variant="h4" sx={{ color: stats.solde > 0 ? COLORS.error : COLORS.success, fontWeight: 800 }}>{stats.solde.toLocaleString()} CFA</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Filters & Search */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: COLORS.gray[200], bgcolor: 'white' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Rechercher par numéro, client..."
                            onChange={(e) => dispatch(filterBillets({ nail: e.target.value }))}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: COLORS.gray[400] }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                    </Grid>
                    
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                        <Grid item xs={12} md={2}>
                            <DatePicker
                                label="Début"
                                value={startDate}
                                onChange={(val) => setStartDate(val)}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <DatePicker
                                label="Fin"
                                value={endDate}
                                onChange={(val) => setEndDate(val)}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </Grid>
                    </LocalizationProvider>

                    <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button 
                                variant="text" 
                                startIcon={<Refresh />} 
                                onClick={() => {
                                    setStartDate(null);
                                    setEndDate(null);
                                    dispatch(fetchBillets());
                                    dispatch(fetchPaiments());
                                }}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Réinitialiser
                            </Button>
                            
                            <BlobProvider document={<JournalVentePDF data={[...billetsWithPayments].reverse()} heads={['numero', 'nom', 'trajet', 'montantAchat', 'tarif', 'montantEncaisse']} title="Caisse" />}>
                                {({ loading, url }) => (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FilePresent />}
                                        href={url}
                                        target="_blank"
                                        disabled={loading}
                                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
                                    >
                                        Imprimer PDF
                                    </Button>
                                )}
                            </BlobProvider>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3}>
                <Grid item xs={12} lg={8}>
                    <Paper elevation={0} sx={{ 
                        borderRadius: '16px', 
                        overflow: 'hidden', 
                        border: '1px solid',
                        borderColor: COLORS.gray[200],
                        bgcolor: 'white'
                    }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: COLORS.gray[100], bgcolor: COLORS.gray[50] }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Détail des flux de caisse
                            </Typography>
                        </Box>
                        
                        {fetchBilletsStatus === 'loading' ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                                <CircularProgress color="primary" />
                            </Box>
                        ) : (
                            <UpdatedTable 
                                rows={[...billetsWithPayments].reverse()} 
                                head={['numero','date', 'nom', 'tarif', 'montantEncaisse', 'methodePaiement', 'commentaire']}
                                totals={['montantEncaisse', 'tarif']} 
                                headSubs={{"montantEncaisse": "Montant Payé", "tarif": "Montant Facturé", "numero": "numero pièces", "nom": "Clients/libellés", "methodePaiement": "Mode de paiement" }}
                            />
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <Paper elevation={0} sx={{ 
                        p: 3,
                        borderRadius: '16px', 
                        border: '1px solid',
                        borderColor: COLORS.gray[200],
                        bgcolor: 'white',
                        height: '100%'
                    }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                            <Assessment color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.secondary }}>
                                Synthèse par Mode
                            </Typography>
                        </Stack>
                        
                        <DashboardTableIII
                            caption={"Récapitulatif des encaissements"}
                            data={paymentGroup}
                            total={priceFormatterV3(_.sumBy(paiements, "montant"))}
                        />
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default Caisse;
